import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useI18n } from '../../contexts/I18nContext';
import {
  typesService,
  HardwareType,
  ModelReference,
  ModelReferenceCreate,
  ModelReferenceUpdate
} from '../../services/typesService';
import { clientService } from '../../services/clientService';
import { Client } from '../../types/client';
import { AddModelReferenceModal } from './AddModelReferenceModal';
import { EditModelReferenceModal } from './EditModelReferenceModal';
import SearchableSelect from '../common/SearchableSelect';

interface ModelReferencesTableProps {
  canEdit?: boolean;
  refreshTrigger?: number;
}

type ViewMode = 'grid' | 'list';

interface ModelRefWithHardware extends ModelReference {
  hardware_type_code: string;
  hardware_type_name: string;
}

export const ModelReferencesTable: React.FC<ModelReferencesTableProps> = ({
  canEdit = false,
  refreshTrigger
}) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { formatDate, t } = useI18n();
  const [hardwareTypes, setHardwareTypes] = useState<HardwareType[]>([]);
  const [modelRefs, setModelRefs] = useState<ModelRefWithHardware[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // View-toggle was dropped (2026-06-02) — list view always. Kept as a
  // const so the grid branch in the JSX below stays one revert away.
  const viewMode = 'list' as ViewMode;
  const [filterHardwareType, setFilterHardwareType] = useState<number | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRef, setSelectedRef] = useState<ModelRefWithHardware | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Client state for display
  const [clientList, setClientList] = useState<Client[]>([]);

  const fetchData = useCallback(async (isMounted: { current: boolean }) => {
    if (!isAuthenticated) {
      if (isMounted.current) setLoading(false);
      return;
    }

    try {
      if (isMounted.current) {
        setLoading(true);
        setError(null);
      }

      const includeInactive = showInactive && canSeeInactive;
      const types = await typesService.getHardwareTypes(includeInactive);
      if (!isMounted.current) return;
      setHardwareTypes(types);

      // Fan-out model-refs fetch in parallel. The sequential version
      // here previously dominated load time on dev DBs with 30+ HW
      // types (one round-trip per type, ~10s end-to-end). Backend
      // has no global /v2/model-references endpoint, so the
      // fan-out has to happen client-side — but in parallel.
      const refsByType = await Promise.all(
        types.map((type) => typesService.getModelReferences(type.id, includeInactive)),
      );
      if (!isMounted.current) return;
      const allRefs: ModelRefWithHardware[] = [];
      types.forEach((type, i) => {
        for (const ref of refsByType[i]) {
          allRefs.push({
            ...ref,
            hardware_type_code: type.code,
            hardware_type_name: type.name,
          });
        }
      });
      setModelRefs(allRefs);
    } catch (err: unknown) {
      console.error('[MODEL REFERENCES TABLE] Error fetching data:', err);
      if (isMounted.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch model references';
        setError(errorMessage);
      }
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [isAuthenticated, showInactive, canSeeInactive]);

  useEffect(() => {
    const isMounted = { current: true };
    fetchData(isMounted);
    return () => { isMounted.current = false; };
  }, [fetchData, refreshTrigger]);

  const refreshData = useCallback(() => {
    const isMounted = { current: true };
    fetchData(isMounted);
  }, [fetchData]);

  // Fetch clients for name lookup
  useEffect(() => {
    const fetchClients = async () => {
      if (!isAuthenticated) return;
      try {
        const clients = await clientService.fetchAllClients();
        setClientList(clients);
      } catch (err) {
        console.error('[MODEL REFERENCES TABLE] Error fetching clients:', err);
      }
    };
    fetchClients();
  }, [isAuthenticated]);

  // Helper function to get client name by ID
  const getClientName = (clientId: string): string => {
    if (clientId === '00000000-0000-0000-0000-000000000000') {
      return 'Global';
    }
    const client = clientList.find(c => c.id === clientId);
    return client?.name || 'Unknown';
  };

  // Helper function to display multiple clients
  const getClientsDisplay = (ref: ModelRefWithHardware): React.ReactNode => {
    const ids = ref.client_ids || ['00000000-0000-0000-0000-000000000000'];
    if (ids.length === 0) return 'None';
    if (ids.length === 1) return getClientName(ids[0]);

    const firstName = getClientName(ids[0]);
    const moreCount = ids.length - 1;
    return (
      <span className="multi-client-display" title={ids.map(getClientName).join(', ')}>
        {firstName} <span className="more-badge">+{moreCount}</span>
      </span>
    );
  };

  // Filter model refs
  const filteredRefs = useMemo(() => {
    return modelRefs.filter((ref) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        ref.code.toLowerCase().includes(searchLower) ||
        (ref.name?.toLowerCase().includes(searchLower) ?? false) ||
        ref.hardware_type_code.toLowerCase().includes(searchLower) ||
        ref.hardware_type_name.toLowerCase().includes(searchLower)
      );
      const matchesFilter = filterHardwareType === 'all' || ref.hardware_type_id === filterHardwareType;
      return matchesSearch && matchesFilter;
    });
  }, [modelRefs, searchTerm, filterHardwareType]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalRefs = modelRefs.length;
    const activeRefs = modelRefs.filter(r => r.is_active).length;
    const inactiveRefs = modelRefs.filter(r => !r.is_active).length;
    const hardwareTypesCount = new Set(modelRefs.map(r => r.hardware_type_id)).size;
    return { totalRefs, activeRefs, inactiveRefs, hardwareTypesCount };
  }, [modelRefs]);

  // Group refs by hardware type for grid view
  const groupedRefs = useMemo(() => {
    const groups: Record<number, { type: HardwareType; refs: ModelRefWithHardware[] }> = {};
    filteredRefs.forEach(ref => {
      if (!groups[ref.hardware_type_id]) {
        const hwType = hardwareTypes.find(t => t.id === ref.hardware_type_id);
        if (hwType) {
          groups[ref.hardware_type_id] = { type: hwType, refs: [] };
        }
      }
      if (groups[ref.hardware_type_id]) {
        groups[ref.hardware_type_id].refs.push(ref);
      }
    });
    return Object.values(groups);
  }, [filteredRefs, hardwareTypes]);

  const handleAddModelRef = async (hardwareTypeId: number, data: ModelReferenceCreate) => {
    await typesService.createModelReference(hardwareTypeId, data);
  };

  const handleEditModelRef = async (id: number, data: ModelReferenceUpdate) => {
    await typesService.updateModelReference(id, data);
  };

  const handleDeleteModelRef = async (ref: ModelRefWithHardware) => {
    if (!window.confirm(`Are you sure you want to delete model reference "${ref.code}"?`)) return;

    setActionError(null);
    try {
      await typesService.deleteModelReference(ref.id);
      refreshData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setActionError(`Failed to delete model reference: ${errorMessage}`);
    }
  };

  const openEditModal = (ref: ModelRefWithHardware) => {
    setSelectedRef(ref);
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="device-types-loading">
        <div className="loading-spinner"></div>
        <p>Loading model references...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="device-types-error">
        <span className="error-icon">!</span>
        <h3>{t('deviceTypes.errorLoadingModelRefs')}</h3>
        <p>{error}</p>
        <button className="retry-button" onClick={refreshData}>{t('errors.retryAction')}</button>
      </div>
    );
  }

  return (
    <div className="device-types-container model-refs-container">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalRefs}</div>
            <div className="stat-label">{t('deviceTypes.modelReferences')}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">&#10003;</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeRefs}</div>
            <div className="stat-label">{t('common.active')}</div>
          </div>
        </div>

        <div className="stat-card warning">
          <div className="stat-icon">&#8856;</div>
          <div className="stat-content">
            <div className="stat-value">{stats.inactiveRefs}</div>
            <div className="stat-label">{t('common.inactive')}</div>
          </div>
        </div>

        <div className="stat-card secondary">
          <div className="stat-icon">&#128295;</div>
          <div className="stat-content">
            <div className="stat-value">{stats.hardwareTypesCount}</div>
            <div className="stat-label">{t('deviceTypes.hardwareTypes')}</div>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <div className="device-types-section model-refs-section">
        <div className="section-header">
          <div className="section-title">
            <h2>{t('deviceTypes.modelReferences')}</h2>
          </div>
          <div className="section-controls">
            {canEdit && (
              <button className="add-type-button" data-testid="btn-add-model-reference" onClick={() => setShowAddModal(true)}>
                + {t('deviceTypes.addModelReference')}
              </button>
            )}
            <SearchableSelect
              className="filter-select"
              data-testid="filter-model-refs-hardware-type"
              ariaLabel={t('deviceTypes.allHardwareTypes')}
              value={filterHardwareType === 'all' ? '' : String(filterHardwareType)}
              onChange={(next) => setFilterHardwareType(next === '' ? 'all' : Number(next))}
              placeholder={t('deviceTypes.allHardwareTypes')}
              options={hardwareTypes.map(type => ({ value: String(type.id), label: `${type.name} (${type.code})` }))}
            />
            <div className="search-container">
              <input
                type="text"
                data-testid="field-model-refs-search"
                placeholder={t('deviceTypes.searchModelRefs')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input search-input--ota-style"
              />
            </div>
            {canSeeInactive && (
              <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                {t('common.showInactive')}
              </label>
            )}
          </div>
        </div>

        {actionError && (
          <div className="action-error-message" style={{ margin: '1rem 1.5rem 0' }}>
            <span className="error-icon">!</span>
            <span>{actionError}</span>
            <button className="dismiss-btn" onClick={() => setActionError(null)} aria-label={t('common.dismissError')}>x</button>
          </div>
        )}

        {viewMode === 'grid' ? (
          <div className="model-refs-grid-container">
            {groupedRefs.map(({ type, refs }) => (
              <div key={type.id} className="model-refs-group">
                <div className="group-header">
                  <span className="group-icon">{type.icon || '&#128295;'}</span>
                  <span className="group-name">{type.name}</span>
                  <span className="group-code">{type.code}</span>
                  <span className="group-count">{refs.length} refs</span>
                </div>
                <div className="model-refs-grid">
                  {refs.map((ref) => (
                    <div
                      key={ref.id}
                      className={`model-ref-card ${!ref.is_active ? 'inactive' : ''}`}
                      onClick={() => canEdit && openEditModal(ref)}
                    >
                      <div className="ref-header">
                        <span className="ref-code">{ref.code}</span>
                        <span className={`status-indicator ${ref.is_active ? 'active' : 'inactive'}`} />
                      </div>
                      <div className="ref-name">{ref.name || t('deviceTypes.noName')}</div>
                      {ref.description && (
                        <div className="ref-description">{ref.description}</div>
                      )}
                      <div className="ref-footer">
                        <span className="ref-updated">
                          {t('deviceTypes.updated')} {formatDate(ref.updated_at)}
                        </span>
                        {canEdit && (
                          <button
                            className="delete-btn"
                            onClick={(e) => { e.stopPropagation(); handleDeleteModelRef(ref); }}
                            title={t('common.delete')}
                          >
                            &#128465;
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="device-types-list model-refs-list">
            <table className="device-types-table model-refs-table">
              <thead>
                <tr>
                  <th>{t('deviceTypes.code')}</th>
                  <th>{t('deviceTypes.name')}</th>
                  <th>{t('deviceTypes.hardwareTypes').slice(0, -1)}</th>
                  <th>{t('deviceTypes.clientsColumn')}</th>
                  {showInactive && <th>{t('deviceTypes.statusColumn')}</th>}
                  <th>{t('deviceTypes.updated')}</th>
                  {canEdit && <th style={{ width: '100px' }}>{t('deviceTypes.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRefs.map((ref) => (
                  <tr
                    key={ref.id}
                    className={`device-type-row ${!ref.is_active ? 'inactive-row' : ''}`}
                  >
                    <td>
                      <span className="type-code">{ref.code}</span>
                    </td>
                    <td className="ref-name-cell">
                      {ref.name || <span className="no-value">-</span>}
                    </td>
                    <td>
                      <span className="hardware-type-badge">
                        {ref.hardware_type_code}
                      </span>
                      <span className="hardware-type-name">{ref.hardware_type_name}</span>
                    </td>
                    <td className="client-cell">
                      {getClientsDisplay(ref)}
                    </td>
                    {showInactive && (
                      <td>
                        <span className={`status-badge ${ref.is_active ? 'active' : 'inactive'}`}>
                          {ref.is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                    )}
                    <td className="date-cell">
                      {formatDate(ref.updated_at)}
                    </td>
                    {canEdit && (
                      <td className="actions-cell">
                        <button
                          className="action-btn edit-btn"
                          onClick={() => openEditModal(ref)}
                          title={t('common.edit')}
                        >
                          &#9998;
                        </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteModelRef(ref)}
                          title={t('common.delete')}
                        >
                          &#128465;
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filteredRefs.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">&#128203;</div>
            <h3>{t('deviceTypes.noModelReferencesFound')}</h3>
            {canEdit && !searchTerm && filterHardwareType === 'all' && (
              <button className="add-type-button" data-testid="btn-add-model-reference" onClick={() => setShowAddModal(true)}>
                + {t('deviceTypes.addModelReference')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Activity Footer */}
      <div className="device-types-footer">
        <div className="activity-summary">
          <div className="activity-item">
            <span className="activity-icon">&#128203;</span>
            <span className="activity-text">
              <strong>{stats.totalRefs}</strong> model references
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">&#10003;</span>
            <span className="activity-text">
              <strong>{stats.activeRefs}</strong> active
            </span>
          </div>
          <div className="activity-item">
            <span className="activity-icon">&#128295;</span>
            <span className="activity-text">
              <strong>{stats.hardwareTypesCount}</strong> hardware types
            </span>
          </div>
        </div>
      </div>

      {/* Add Model Reference Modal */}
      <AddModelReferenceModal
        isOpen={showAddModal}
        hardwareTypes={hardwareTypes}
        onClose={() => setShowAddModal(false)}
        onCreated={refreshData}
        onCreate={handleAddModelRef}
      />

      {/* Edit Model Reference Modal */}
      <EditModelReferenceModal
        isOpen={showEditModal}
        modelRef={selectedRef}
        onClose={() => {
          setShowEditModal(false);
          setSelectedRef(null);
        }}
        onUpdated={refreshData}
        onUpdate={handleEditModelRef}
      />
    </div>
  );
};
